import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";

const numFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const priceFmt = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const usdFmt = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const inrFmt = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });

const CCY_SYMBOL = { USD: "$", INR: "₹", EURO: "€", EUR: "€", GBP: "£", POUND: "£", CAD: "C$" };
const sym = (c) => CCY_SYMBOL[c] || `${c} `;
const money = (v, c) => (v == null ? "—" : `${sym(c)}${priceFmt.format(v)}`);
const unitLabel = (u) => (u === "LBS" ? "lb" : u === "KGS" ? "kg" : u === "RINGS" ? "ring" : u.toLowerCase());

function csvEscape(value) {
  if (value === null || value === undefined) return "";
  const s = String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function StatTile({ label, value, sub, accent }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold" style={{ color: accent || "#0f172a" }}>{value}</div>
      {sub ? <div className="mt-1 text-xs text-slate-500">{sub}</div> : null}
    </div>
  );
}

export default function SalesPriceAnalysisDetail() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [detail, setDetail] = useState(null);
  const [options, setOptions] = useState({ buyers: [], countries: [], varieties: [] });
  // Currency and unit choices come from an unfiltered load and stay fixed, so
  // picking one currency never hides the others from the dropdown.
  const [ccyUnitOptions, setCcyUnitOptions] = useState({ currencies: [], units: [] });
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(true);
  const [error, setError] = useState("");
  const [detailError, setDetailError] = useState("");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [currency, setCurrency] = useState("");
  const [unit, setUnit] = useState("");
  const [buyer, setBuyer] = useState("");
  const [country, setCountry] = useState("");
  const [variety, setVariety] = useState("");
  const [search, setSearch] = useState("");

  // Overlapping filter requests can resolve out of order; the guards make sure
  // only the newest answer is shown.
  const summarySeq = useRef(0);
  const detailSeq = useRef(0);

  useEffect(() => {
    api.get("/ceo/filter-options")
      .then((r) => setOptions({
        buyers: r.data?.buyers || [], countries: r.data?.countries || [], varieties: r.data?.varieties || [],
      }))
      .catch(() => setOptions({ buyers: [], countries: [], varieties: [] }));
    api.post("/ai/ceo-chat/sales-price-analysis", {})
      .then((r) => {
        const rows = r.data?.by_currency || [];
        setCcyUnitOptions({
          currencies: Array.from(new Set(rows.map((x) => x.currency))),
          units: Array.from(new Set(rows.map((x) => x.unit))),
        });
      })
      .catch(() => setCcyUnitOptions({ currencies: [], units: [] }));
  }, []);

  const body = useMemo(
    () => ({ from_date: fromDate, to_date: toDate, currency, unit, buyer, country, variety }),
    [fromDate, toDate, currency, unit, buyer, country, variety]
  );

  const load = useCallback(async () => {
    const seq = ++summarySeq.current;
    setLoading(true); setError("");
    try {
      const r = await api.post("/ai/ceo-chat/sales-price-analysis", body);
      if (seq !== summarySeq.current) return;
      setSummary(r.data);
    } catch (err) {
      if (seq !== summarySeq.current) return;
      setError(formatErr(err));
    } finally {
      if (seq === summarySeq.current) setLoading(false);
    }
  }, [body]);

  const loadDetail = useCallback(async () => {
    const seq = ++detailSeq.current;
    setDetailLoading(true); setDetailError("");
    try {
      const r = await api.post("/ai/ceo-chat/sales-price-analysis/detail", { ...body, limit: 2000 });
      if (seq !== detailSeq.current) return;
      setDetail(r.data);
    } catch (err) {
      if (seq !== detailSeq.current) return;
      setDetailError(formatErr(err));
    } finally {
      if (seq === detailSeq.current) setDetailLoading(false);
    }
  }, [body]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  const totals = summary?.totals || {};
  const byCcy = useMemo(() => summary?.by_currency || [], [summary]);
  const usdRows = byCcy.filter((x) => x.currency === "USD");
  const otherRows = byCcy.filter((x) => x.currency !== "USD");
  const usdPerLb = usdRows.find((x) => x.unit === "LBS");
  const usdPerKg = usdRows.find((x) => x.unit === "KGS");
  const trend = useMemo(() => summary?.usd_price_trend || [], [summary]);
  const trendMonths = useMemo(() => Array.from(new Set(trend.map((t) => t.month))).sort(), [trend]);
  const trendBy = (m, u) => trend.find((t) => t.month === m && t.unit === u);
  const byVariety = summary?.usd_price_by_variety || [];

  const filteredDetail = useMemo(() => {
    const list = detail?.rows || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((r) =>
      [r.invoice_no, r.po_no, r.buyer, r.consignee, r.country, r.variety, r.grade, r.packing_style]
        .join(" ").toLowerCase().includes(q));
  }, [detail, search]);

  const exportCsv = useCallback(() => {
    const head = ["Invoice No", "Invoice Date", "Order No", "Buyer", "Consignee", "Country", "Variety", "Grade",
      "Packing", "Cartons", "Quantity", "Unit", "Price per Unit", "Currency", "Value (native)",
      "Rate to INR", "Value INR", "Value USD (USD lines only)"];
    const lines = [head.join(",")].concat(filteredDetail.map((r) => [
      r.invoice_no, r.invoice_date, r.po_no, r.buyer, r.consignee, r.country, r.variety, r.grade,
      r.packing_style, r.mcs, r.quantity, r.unit, r.price, r.currency, r.value_native,
      r.ex_rate ?? "", r.value_inr ?? "", r.value_usd ?? "",
    ].map(csvEscape).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "sales-price-by-invoice-line.csv"; a.click();
    URL.revokeObjectURL(url);
  }, [filteredDetail]);

  const resetFilters = () => { setFromDate(""); setToDate(""); setCurrency(""); setUnit(""); setBuyer(""); setCountry(""); setVariety(""); };

  const selectCls = "mt-1 block rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800";
  const labelCls = "text-xs font-semibold uppercase tracking-wide text-slate-500";

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-[1500px] space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="max-w-3xl">
            <h1 className="text-xl font-semibold text-slate-900">Selling Price &amp; Invoice Value — by Currency</h1>
            <p className="mt-1 text-sm text-slate-600">
              What we are selling at, per pound or per kilo, and what each invoice is worth. Prices are
              per weight unit, not per carton, and every figure stays in the currency it was invoiced in —
              dollars are never added to rupees or euros.
            </p>
          </div>
          <button type="button" onClick={() => navigate("/ceo-command-center?tab=commercial")}
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            Back to Sales
          </button>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-end gap-3">
            <label className={labelCls}>From Date
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={selectCls} /></label>
            <label className={labelCls}>To Date
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={selectCls} /></label>
            <label className={labelCls}>Currency
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={selectCls}>
                <option value="">All Currencies</option>
                {ccyUnitOptions.currencies.map((c) => <option key={c} value={c}>{c === "UNMAPPED" ? "Not recorded" : c}</option>)}
              </select></label>
            <label className={labelCls}>Price Unit
              <select value={unit} onChange={(e) => setUnit(e.target.value)} className={selectCls}>
                <option value="">All Units</option>
                {ccyUnitOptions.units.map((u) => <option key={u} value={u}>{u}</option>)}
              </select></label>
            <label className={labelCls}>Buyer
              <select value={buyer} onChange={(e) => setBuyer(e.target.value)} className={`${selectCls} max-w-[200px]`}>
                <option value="">All Buyers</option>
                {options.buyers.map((b) => <option key={b} value={b}>{b}</option>)}
              </select></label>
            <label className={labelCls}>Country
              <select value={country} onChange={(e) => setCountry(e.target.value)} className={`${selectCls} max-w-[170px]`}>
                <option value="">All Countries</option>
                {options.countries.map((c) => <option key={c} value={c}>{c}</option>)}
              </select></label>
            <label className={labelCls}>Variety
              <select value={variety} onChange={(e) => setVariety(e.target.value)} className={selectCls}>
                <option value="">All Varieties</option>
                {options.varieties.map((v) => <option key={v} value={v}>{v}</option>)}
              </select></label>
            <button type="button" onClick={resetFilters}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Reset Filters</button>
          </div>
          <div className="mt-2 text-xs text-slate-500">
            {summary?.date_basis ? `Date basis: ${summary.date_basis}.` : "Date basis: invoice date."}{" "}
            {!fromDate && !toDate ? "No date filter applied — the full invoice history is shown." : ""}
          </div>
        </div>

        {error ? <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">{error}</div> : null}
        {loading ? <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">Loading…</div> : null}

        {!loading && summary ? (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <StatTile label="USD Sales Value" value={usdFmt.format(totals.usd_value || 0)}
                sub={`${numFmt.format(totals.usd_lines || 0)} invoice lines invoiced in USD`} accent="#2563eb" />
              <StatTile label="Avg USD Price per lb" value={usdPerLb?.avg_price != null ? `$${priceFmt.format(usdPerLb.avg_price)}` : "—"}
                sub={usdPerLb ? `${numFmt.format(usdPerLb.quantity)} lb invoiced` : "No USD sales priced per lb"} />
              <StatTile label="Avg USD Price per kg" value={usdPerKg?.avg_price != null ? `$${priceFmt.format(usdPerKg.avg_price)}` : "—"}
                sub={usdPerKg ? `${numFmt.format(usdPerKg.quantity)} kg invoiced` : "No USD sales priced per kg"} />
              <StatTile label="Other Currencies" value={numFmt.format((totals.excluded_from_usd || []).reduce((a, x) => a + x.sale_lines, 0))}
                sub="Invoice lines kept out of the USD total" accent={otherRows.length ? "#d97706" : undefined} />
              <StatTile label="Value in INR" value={inrFmt.format(totals.inr_value_all_currencies || 0)}
                sub={totals.inr_lines_without_rate ? `${numFmt.format(totals.inr_lines_without_rate)} line(s) have no invoice rate and are left out` : "Every line converted at its invoice rate"} />
            </div>

            {otherRows.length ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                <div className="text-sm font-semibold text-amber-900">Sales invoiced in other currencies — shown separately, never added to the USD figure</div>
                <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {otherRows.map((x) => (
                    <div key={`${x.currency}-${x.unit}`} className="rounded-xl border border-amber-200 bg-white p-3 text-sm">
                      <div className="font-semibold text-slate-900">
                        {x.currency === "UNMAPPED" ? "Currency not recorded" : x.currency} · per {unitLabel(x.unit)}
                      </div>
                      <div className="mt-1 text-slate-700">{money(x.value_native, x.currency)} across {numFmt.format(x.sale_lines)} line(s)</div>
                      <div className="text-xs text-slate-500">
                        Avg {money(x.avg_price, x.currency)}/{unitLabel(x.unit)}
                        {x.value_inr != null ? ` · ${inrFmt.format(x.value_inr)} at invoice rate` : " · no invoice rate on file"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {trendMonths.length ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-sm font-semibold text-slate-900">USD selling price by month</div>
                <div className="mt-1 text-xs text-slate-500">Weighted by quantity invoiced that month. Per-lb and per-kg orders are priced differently and are kept apart.</div>
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead><tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-3">Month</th>
                      <th className="py-2 pr-3 text-right">$ per lb</th><th className="py-2 pr-3 text-right">lb invoiced</th>
                      <th className="py-2 pr-3 text-right">$ per kg</th><th className="py-2 pr-3 text-right">kg invoiced</th>
                      <th className="py-2 pr-3 text-right">USD value</th>
                    </tr></thead>
                    <tbody>
                      {trendMonths.slice(-18).map((m) => { const lb = trendBy(m, "LBS"), kg = trendBy(m, "KGS"); return (
                        <tr key={m} className="border-b border-slate-100">
                          <td className="py-2 pr-3 text-slate-800">{m}</td>
                          <td className="py-2 pr-3 text-right font-medium text-slate-900">{lb?.avg_price != null ? `$${priceFmt.format(lb.avg_price)}` : "—"}</td>
                          <td className="py-2 pr-3 text-right text-slate-600">{lb ? numFmt.format(lb.quantity) : "—"}</td>
                          <td className="py-2 pr-3 text-right font-medium text-slate-900">{kg?.avg_price != null ? `$${priceFmt.format(kg.avg_price)}` : "—"}</td>
                          <td className="py-2 pr-3 text-right text-slate-600">{kg ? numFmt.format(kg.quantity) : "—"}</td>
                          <td className="py-2 pr-3 text-right text-slate-800">{usdFmt.format((lb?.value_usd || 0) + (kg?.value_usd || 0))}</td>
                        </tr>); })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}

            {byVariety.length ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-sm font-semibold text-slate-900">USD selling price by product</div>
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead><tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-3">Product</th><th className="py-2 pr-3">Priced per</th>
                      <th className="py-2 pr-3 text-right">Avg price</th><th className="py-2 pr-3 text-right">Quantity</th>
                      <th className="py-2 pr-3 text-right">USD value</th><th className="py-2 pr-3 text-right">Lines</th>
                    </tr></thead>
                    <tbody>{byVariety.map((v) => (
                      <tr key={`${v.variety}-${v.unit}`} className="border-b border-slate-100">
                        <td className="py-2 pr-3 text-slate-800">{v.variety}</td>
                        <td className="py-2 pr-3 text-slate-600">{unitLabel(v.unit)}</td>
                        <td className="py-2 pr-3 text-right font-medium text-slate-900">{v.avg_price != null ? `$${priceFmt.format(v.avg_price)}` : "—"}</td>
                        <td className="py-2 pr-3 text-right text-slate-600">{numFmt.format(v.quantity)} {unitLabel(v.unit)}</td>
                        <td className="py-2 pr-3 text-right text-slate-800">{usdFmt.format(v.value_usd)}</td>
                        <td className="py-2 pr-3 text-right text-slate-600">{numFmt.format(v.sale_lines)}</td>
                      </tr>))}</tbody>
                  </table>
                </div>
              </div>
            ) : null}

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Invoice lines</div>
                  <div className="mt-1 text-xs text-slate-500">
                    {detailLoading ? "Loading…" : detail?.truncated
                      ? `Showing ${numFmt.format(filteredDetail.length)} of ${numFmt.format(detail.matching_lines || 0)} lines — narrow the filters to see them all`
                      : `${numFmt.format(filteredDetail.length)} line(s)`}
                  </div>
                </div>
                <div className="flex gap-2">
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search invoice, buyer, product"
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                  <button type="button" onClick={exportCsv}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Export CSV</button>
                </div>
              </div>
              {detailLoading ? <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 p-6 text-center text-sm text-slate-500">Loading invoice lines…</div> : null}
              {detailError ? <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">The invoice lines could not be loaded: {detailError} The figures above are unaffected.</div> : null}
              <div className={`mt-3 overflow-x-auto${detailLoading || detailError ? " hidden" : ""}`}>
                <table className="w-full text-left text-sm">
                  <thead><tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-3">Invoice</th><th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Buyer</th>
                    <th className="py-2 pr-3">Product</th><th className="py-2 pr-3">Packing</th>
                    <th className="py-2 pr-3 text-right">Cartons</th><th className="py-2 pr-3 text-right">Quantity</th>
                    <th className="py-2 pr-3 text-right">Price</th><th className="py-2 pr-3 text-right">Value</th>
                    <th className="py-2 pr-3 text-right">Value INR</th>
                  </tr></thead>
                  <tbody>{filteredDetail.slice(0, 300).map((r, i) => (
                    <tr key={`${r.line_id}-${i}`} className="border-b border-slate-100">
                      <td className="py-2 pr-3 font-medium text-slate-800">{r.invoice_no}</td>
                      <td className="py-2 pr-3 text-slate-600">{r.invoice_date}</td>
                      <td className="py-2 pr-3 text-slate-700">{r.buyer}</td>
                      <td className="py-2 pr-3 text-slate-700">{r.variety}{r.grade ? ` · ${r.grade}` : ""}</td>
                      <td className="py-2 pr-3 text-slate-600">{r.packing_style}</td>
                      <td className="py-2 pr-3 text-right text-slate-700">{numFmt.format(r.mcs)}</td>
                      <td className="py-2 pr-3 text-right text-slate-700">{numFmt.format(r.quantity)} {unitLabel(r.unit)}</td>
                      <td className="py-2 pr-3 text-right font-medium text-slate-900">{money(r.price, r.currency)}/{unitLabel(r.unit)}</td>
                      <td className="py-2 pr-3 text-right text-slate-800">
                        <span className={r.currency !== "USD" ? "rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-800" : ""}>
                          {money(r.value_native, r.currency)}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-right text-slate-600">{r.value_inr != null ? inrFmt.format(r.value_inr) : "no rate"}</td>
                    </tr>))}</tbody>
                </table>
                {filteredDetail.length > 300 ? <div className="mt-2 text-xs text-slate-500">Showing the first 300 rows on screen. Export CSV includes all {numFmt.format(filteredDetail.length)}.</div> : null}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 text-xs text-slate-500 shadow-sm">
              <div className="font-semibold text-slate-700">How this is calculated</div>
              <div className="mt-1">
                Quantity = cartons × slabs per carton × slab weight, converted to the order's price unit from the packing
                style master. Value = quantity × price, in the invoice currency — the same arithmetic the ERP customs invoice
                uses. INR value = value × the exchange rate recorded on that invoice. USD figures include only lines invoiced
                in USD; ERP has no rule for converting other currencies into dollars, so those are shown in their own currency.
              </div>
              <div className="mt-2">Totals reconcile: {summary?.reconciliation?.ok ? "yes" : "not verified"} ({summary?.reconciliation?.equation}).</div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
